<template>
    <section id="about" class="about-component page-section">
        <div class="container">
            <div class="title-container">
                <header class="main-title title">About me</header>
                <div class="profile-img-container">
                    <img alt="my photo" src="@/assets/about/my-photo-black-and-white.jpeg">
                </div>
                <div class="title-info">
                    <header class="section-title">Summary</header>
                    <p class="summary-text">Senior Software Engineer with {{ yearsOfExperience }} years of full stack experience, with a focus on JavaScript technologies such as Vue.js and Node.js. Most recently designed and built AI solutions in Python and Vue.js at JFrog, including the JFrog MCP service. Before that, built a cloud ticketing platform used by most cinemas in Israel and by cinemas in six European countries, and ERP SaaS for the construction industry. Led a team of 4 developers with code reviews, mentoring and Agile workflows.</p>
                </div>
            </div>
            <div class="timeline-content" :class="{'reveal-ready': observing}">
                <div class="timeline">
                    <div class="timeline-image jfrog-image">
                        <a class="logo-link" href="https://jfrog.com/" target="_blank" rel="noopener" aria-label="JFrog website">
                            <img alt="logo" src="@/assets/about/jfrog.png">
                        </a>
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2026:</header>
                                <header class="timeline-sub-title">Senior Software Engineer at JFrog</header>
                                <header class="timeline-sub-title info">Engaged via AgileSparks</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed the JFrog MCP service, exposing platform capabilities to AI agents and LLM-based tools via the Model Context Protocol</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Designed and implemented workflows that automate multi-step, AI-driven operations within the service</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed backend logic in Python and reusable Vue 3 components, covering both the server and client sides of the service</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Collaborated with other JFrog teams to implement end-to-end scenarios spanning multiple platform services</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Worked daily with AI coding tools such as Claude Code and Cursor, including agent skills and multi-agent workflows, alongside hands-on development across a wide range of engineering tasks</div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">Python, Vue.js, Micro Frontends, AWS, Azure, Docker, Kubernetes, PostgreSQL, CI/CD, GitHub Actions, Jenkins, SonarQube, Grafana, MCP, LLMs, AI Agents, Claude Code, Cursor</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-image presglobal-image">
                        <a class="logo-link" href="https://www.pres.global/" target="_blank" rel="noopener" aria-label="Pres Global website">
                            <img alt="logo" src="@/assets/about/pg-logo.svg">
                        </a>
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2024 - 2026:</header>
                                <header class="timeline-sub-title">Senior Full Stack Developer at PresGlobal</header>
                                <header class="timeline-sub-title info">Cloud ticketing platform for cinemas, theatres and venues</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed and supported an end-to-end cinema management platform covering scheduling, venues, operations, ticketing, reporting, and customer service, used by most cinemas in Israel and by cinemas in Poland, Czechia, Slovakia, Hungary, Romania and Bulgaria</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed responsive cinema UI for major cinema chains including Cinema City, Lev Cinemas, Planet, and international cinema networks</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Integrated PayMe payments into the ticketing platform</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Improved performance by introducing new technologies and maintained scalable backend services</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed box office and self-service kiosk systems, sales and management workflows, and ticket validation via entry scanners</div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">Vue.js, Nuxt.js, Node.js, MySQL, Redis, Docker, Kubernetes, Microservices, OpenSearch, BetterStack, AWS, Git (GitHub, Bitbucket), Augment (AI agents & workflow automation)</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-image probuild-image">
                        <a class="logo-link" href="https://www.probuild.co.il/" target="_blank" rel="noopener" aria-label="Probuild website">
                            <img alt="logo" src="@/assets/about/probuild.png">
                        </a>
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2021 - 2024:</header>
                                <header class="timeline-sub-title">Full Stack Team Leader at Probuild</header>
                                <header class="timeline-sub-title info">ERP SaaS for construction industry</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Led a team of 4 developers while developing hands-on across the full stack of an ERP SaaS platform
                                    </div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Conducted code reviews and mentored team members</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Established Agile workflows in close collaboration with product owners and QA teams, speeding up and streamlining the team's delivery process</div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">Vue.js, JavaScript, HTML5, SCSS, Node.js, Express.js, PHP, MongoDB, RabbitMQ, Kibana, AWS, Git, GitHub Copilot, Jira, Confluence</span>
                            </div>
                        </div>
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2020 - 2021:</header>
                                <header class="timeline-sub-title">Web Developer at Probuild</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed ERP software modules for project management, contractors, finance, supply chain, and HR
                                    </div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">Vue.js, JavaScript, HTML5, SCSS, Node.js, PHP, MongoDB, RabbitMQ, Kibana, AWS, Git, GitHub Copilot</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-image">
                        <a class="logo-link" href="https://hippotec.com/" target="_blank" rel="noopener" aria-label="Hippotec website">
                            <img alt="logo" src="@/assets/about/hippotec.png">
                        </a>
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2016 - 2020:</header>
                                <header class="timeline-sub-title">Web Developer at Hippotec</header>
                                <header class="timeline-sub-title info">Software development studio</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed projects for multiple clients, including an international medical services platform (AirDoctor), a video-based social platform, a CRM system, and a municipal transportation rides management system</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Developed the Probuild construction ERP (client and server) as a client project, before joining Probuild in-house in 2020</div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">Vue.js, AngularJS, Knockout.js, HTML5, SCSS, Bootstrap, Node.js, PHP, MongoDB</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-image">
                        <img alt="logo" src="@/assets/about/freelancer.jpg">
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2015 - 2016:</header>
                                <header class="timeline-sub-title">Web Developer, Freelance</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Full stack web development for business websites and eCommerce projects
                                    </div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">HTML, CSS, LESS, JavaScript, jQuery, Bootstrap, PHP, MySQL, WordPress</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-image">
                        <a class="logo-link" href="https://www.sap.com/israel/products/acquired-brands/what-is-gigya.html" target="_blank" rel="noopener" aria-label="Gigya website">
                            <img alt="logo" src="@/assets/about/gigya.jpg">
                        </a>
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2010 - 2015:</header>
                                <header class="timeline-sub-title">Network Operations Center Engineer at Gigya</header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Monitored customer identity and access management (CIAM) client-server systems in the Network Operations Center (NOC), working with logs and alerts</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Provided technical support and performed manual QA</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Worked closely with QA and R&amp;D teams</div>
                                </div>
                            </div>
                            <div class="tech-stack">
                                <span class="tech-label">Tech: </span>
                                <span class="tech-items">Kibana, Fiddler, Manual QA</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-image">
                        <a class="logo-link" href="https://www.hit.ac.il/" target="_blank" rel="noopener" aria-label="Holon Institute of Technology website">
                            <img alt="logo" class="hit" src="@/assets/about/hit.jpg">
                        </a>
                    </div>
                    <div class="timeline-panel">
                        <div class="one-role">
                            <div class="timeline-title-container">
                                <header class="timeline-title">2009 - 2013:</header>
                                <header class="timeline-sub-title">B.Sc.,Computer Science (Holon Institute of
                                    Technology)
                                </header>
                            </div>
                            <div class="timeline-body">
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Web programming: JavaScript, HTML, CSS, MySQL</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Mobile programming: Android, jQuery mobile</div>
                                </div>
                                <div>
                                    <font-awesome-icon class="icon" :icon="['fas', 'chevron-circle-right']"/>
                                    <div>Programming: C / C ++ / C#.NET, Java / J2EE, Scala</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="cv-container">
                <a href="/CV/CV_Alexander_Gorbatovsky.pdf" class="download" download="CV_Alexander_Gorbatovsky">Download
                    CV</a>
                <!--        <a href="/CV/CV-hebrew.pdf" class="download" download="CV-hebrew Alexander Gorbatovsky">Download CV in-->
                <!--          Hebrew</a>-->
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: "AboutComponent",
    data() {
        return {
            observing: false
        };
    },
    mounted() {
        this.setupReveal();
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        // Timeline entries slide in once, when they first enter the viewport.
        setupReveal() {
            const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (reduceMotion || !('IntersectionObserver' in window)) {
                return;
            }
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                        this.observer.unobserve(entry.target);
                    }
                });
            }, {threshold: 0.15});
            this.$el.querySelectorAll('.timeline-content .timeline').forEach((el) => this.observer.observe(el));
            this.observing = true;
        }
    },
    computed: {
        yearsOfExperience() {
            const startYear = 2016;
            return new Date().getFullYear() - startYear;
        }
    }
}
</script>

<style lang="scss" scoped>
#about.about-component {
    display: flex;
    justify-content: center;
    align-items: center;

    .container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        max-width: 1200px;
        width: 100%;

        .title-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            .title {
                margin-bottom: calc(var(--section-title-gap, 50px) - 10px);

                &.prof {
                    font-family: "Lobster", Lobster, Arial, sans-serif;
                    font-size: 2em;
                }
            }

            .profile-img-container {
                width: 200px;
                height: 200px;
                display: flex;
                color: #fff;
                border: 7px solid #e9ecef;
                border-radius: 50%;
                background-color: #2c3e50;
                margin: 10px;

                img {
                    max-width: 100%;
                    object-fit: cover;
                    border-radius: 50%;
                }
            }

            .title-info {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                max-width: 900px;
                margin-top: 20px;

                .summary-text {
                    line-height: 24px;
                    text-align: center;
                    margin: 0;
                }
            }
        }

        .section-title {
            font-family: "Lobster", Lobster, Arial, sans-serif;
            font-size: 1.6em;
            margin-bottom: 10px;
            text-align: center;
        }

        .timeline-content {
            position: relative;
            width: 100%;
            margin-top: 50px;

            .timeline {
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                min-height: 280px;
                width: 100%;
                position: relative;

                .timeline-image {
                    position: absolute;
                    width: 170px;
                    height: 170px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    border: 7px solid #e9ecef;
                    border-radius: 50%;
                    background-color: #2c3e50;
                    top: 0;
                    left: calc(50% - 85px);
                    font-family: 'Lobster', Lobster, Arial, sans-serif;
                    font-size: 35px;

                    img {
                        max-width: 100%;
                        height: 100%;
                        object-fit: cover;
                        border-radius: 50%;
                    }

                    .logo-link {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        transition: box-shadow .25s ease;

                        &:hover, &:focus-visible {
                            outline: none;
                            box-shadow: 0 0 0 4px #2c3e50;
                        }
                    }

                    &.probuild-image {
                        background-color: #ffffff;

                        img {
                            max-width: 90%;
                            object-fit: contain;
                        }
                    }

                    &.jfrog-image,
                    &.presglobal-image {
                        background-color: #ffffff;

                        img {
                            max-width: 90%;
                            object-fit: contain;
                        }
                    }
                }

                .timeline-panel {
                    width: 100%;

                    .one-role {
                        margin-top: 2em;
                    }

                    .timeline-title-container {
                        font-size: 1.2em;
                        margin-bottom: 0.3em;
                        font-weight: bold;

                        .timeline-title {
                            font-weight: 100;
                        }

                        .timeline-sub-title {
                            &.info {
                                color: #979a9c;
                                font-weight: 100;
                            }
                        }
                    }

                    .timeline-body {
                        line-height: 22px;

                        div {
                            display: flex;
                            align-items: flex-start;
                            margin: 2px 0;

                            .icon {
                                flex-shrink: 0;
                                font-size: 0.9em;
                                margin-top: 5px;
                                margin-inline-end: 6px;
                            }
                        }
                    }

                    .tech-stack {
                        margin-top: 10px;
                        line-height: 1.5;

                        .tech-label {
                            font-weight: bold;
                            color: #2c3e50;
                        }

                        .tech-items {
                            color: #555;
                            font-weight: 600;
                        }
                    }
                }

                &:before {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: calc(50% - 2px);
                    width: 2px;
                    content: "";
                    background-color: #e9ecef;
                }

                &:first-child {
                    &:before {
                        top: 0;
                    }
                }

                &:last-child {
                    &:before {
                        bottom: 100%;
                    }
                }

                &:nth-child(odd) {
                    .timeline-panel {
                        padding-inline-end: calc(50% + 85px);
                    }
                }

                &:nth-child(even) {
                    .timeline-panel {
                        padding-inline-start: calc(50% + 140px);
                    }
                }

            }
        }

        .timeline-content.reveal-ready {
            .timeline {
                .timeline-image,
                .timeline-panel {
                    opacity: 0;
                    transition: opacity .6s ease-out, transform .6s ease-out;
                }

                .timeline-image {
                    transform: scale(.85);
                }

                // Panels come in from the side they sit on.
                &:nth-child(odd) .timeline-panel {
                    transform: translateX(-28px);
                }

                &:nth-child(even) .timeline-panel {
                    transform: translateX(28px);
                }

                &.visible {
                    .timeline-image,
                    .timeline-panel {
                        opacity: 1;
                        transform: none;
                    }
                }
            }
        }

        .cv-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-top: 40px;
        }
    }
}

/*media*/
@media screen and (max-width: 1200px) {
    #about.about-component {
        .container {
            .timeline-content {
                .timeline {
                    .timeline-image {
                        width: 140px;
                        height: 140px;
                        left: calc(50% - 71px);
                    }

                    &:nth-child(odd) {
                        .timeline-panel {
                            padding-inline-end: calc(50% + 85px);
                        }
                    }

                    &:nth-child(even) {
                        .timeline-panel {
                            padding-inline-start: calc(50% + 70px);
                        }
                    }
                }
            }
        }
    }
}

@media screen and (max-width: 1000px) {
    #about.about-component {
        .container {
            .timeline-content.reveal-ready {
                .timeline {
                    &:nth-child(odd), &:nth-child(even) {
                        .timeline-panel {
                            transform: translateY(20px);
                        }
                    }

                    &.visible {
                        &:nth-child(odd), &:nth-child(even) {
                            .timeline-panel {
                                transform: none;
                            }
                        }
                    }
                }
            }

            .timeline-content {
                .timeline {
                    min-height: 250px;

                    .timeline-image {
                        width: 120px;
                        height: 120px;
                        left: 0;
                    }

                    &:nth-child(odd), &:nth-child(even) {
                        .timeline-panel {
                            padding-inline-start: 140px;
                            padding-inline-end: 0;
                        }
                    }

                    &:before {
                        left: 60px;
                    }
                }
            }
        }
    }
}

@media screen and (max-width: 770px) {
    #about.about-component {
        .container {

            .title-container {
                .profile-img-container {
                    width: 150px;
                    height: 150px;
                }
            }

            .timeline-content {
                .timeline {
                    .timeline-image {
                        width: 100px;
                        height: 100px;
                        left: 0;
                    }

                    .timeline-panel {
                        .timeline-title-container {
                            font-size: 1em;
                        }

                        .timeline-body {
                            font-size: 1em;
                        }
                    }

                    &:nth-child(odd), &:nth-child(even) {
                        .timeline-panel {
                            padding-inline-start: 115px;
                        }
                    }

                    &:last-child {


                        .timeline-image {

                        }
                    }

                    &:before {
                        left: 50px;
                    }
                }
            }
        }
    }
}
</style>